import React, { lazy, Suspense } from 'react';
import { connect } from 'react-redux';

import {
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { NotificationContainer } from 'react-notifications';
import { setDistanceToBottom } from 'state/ducks/window';

import ThemeProvider from 'providers/ThemeProvider';
import DarkModeProvider from 'providers/DarkModeProvider';
import FlagsProvider from 'providers/FlagsProvider';
import { ConfirmProvider } from 'material-ui-confirm';
import { useRefreshToken, useToken } from 'utils/contexts/app-context';
import { BillingProvider } from 'utils/contexts/billing-context';
import ScrollToTop from 'utils/ScrollToTop';
import { AUTH, PRIVATE, PUBLIC } from 'router/routes';
import Routers from 'router';
import PageLoadingFallback from 'components/page-loading-fallback';

import 'App.scss';
import 'react-notifications/lib/notifications.css';
import 'styles/notifications-overrides.scss';

const Login = lazy(() => import('containers/login'));
const Register = lazy(() => import('containers/register'));
const CognitoOAuthPopup = lazy(() => import('containers/auth/CognitoOAuthPopup'));
const Activate = lazy(() => import('containers/activate'));
const ResetPassword = lazy(() => import('containers/reset-password'));
const MagicLinkVerify = lazy(() => import('containers/login/MagicLinkVerify/MagicLinkVerify'));
const MfaVerify = lazy(() => import('containers/mfa-verify'));
const Terms = lazy(() => import('containers/terms'));
const Privacy = lazy(() => import('containers/privacy'));
const GlobalAgentChat = lazy(() => import('components/AgentChat/GlobalAgentChat'));

const App = ({ defaultFlags = [] }) => {
  const refreshToken = useRefreshToken();
  const token = useToken();
  const location = useLocation();

  const flags = Array.isArray(defaultFlags) ? [...defaultFlags] : [];

  const { PublicRouter, PrivateRouter, AdminRouter } = Routers;
  const showGlobalAgentChat =
    Boolean(token) &&
    (location.pathname.startsWith('/app') ||
      location.pathname.startsWith('/admin'));

  return (
    <>
        <FlagsProvider value={flags}>
        <DarkModeProvider>
          <ThemeProvider>
            <ConfirmProvider>
              <ScrollToTop />
              <Routes>
                <Route
                  path="/app/*"
                  element={
                    token ? (
                      <BillingProvider>
                        <PrivateRouter />
                      </BillingProvider>
                    ) : (
                      <Navigate to={AUTH.login} replace />
                    )
                  }
                />

                <Route
                  path="/admin/*"
                  element={
                    token ? (
                      <BillingProvider>
                        <AdminRouter />
                      </BillingProvider>
                    ) : (
                      <Navigate to={AUTH.login} replace />
                    )
                  }
                />

                <Route path={AUTH.magic_link} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><MagicLinkVerify /></Suspense>
                } />
                <Route path={AUTH.cognitoPopup} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><CognitoOAuthPopup /></Suspense>
                } />
                <Route path={AUTH.register} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><Register /></Suspense>
                } />
                <Route path={AUTH.mfaVerify} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><MfaVerify /></Suspense>
                } />

                {(!refreshToken || !token) && (
                  <>
                    <Route path={AUTH.login} element={
                      <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><Login /></Suspense>
                    } />
                    <Route path={AUTH.activate} element={
                      <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><Activate /></Suspense>
                    } />
                    <Route path={AUTH.reset_password} element={
                      <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><ResetPassword /></Suspense>
                    } />
                  </>
                )}

                <Route path={PUBLIC.terms_of_use} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><Terms /></Suspense>
                } />
                <Route path={PUBLIC.privacy_policy} element={
                  <Suspense fallback={<PageLoadingFallback minHeight="100vh" />}><Privacy /></Suspense>
                } />
                <Route path={PUBLIC.website} element={<PublicRouter />} />

                <Route
                  path="*"
                  element={(
                    <Navigate
                      to={{
                        pathname: !refreshToken || !token ? AUTH.login : '/app',
                        hash: location.hash,
                      }}
                      replace
                    />
                  )}
                />
              </Routes>

              {showGlobalAgentChat ? (
                <Suspense fallback={null}>
                  <GlobalAgentChat />
                </Suspense>
              ) : null}

              <NotificationContainer />
            </ConfirmProvider>
          </ThemeProvider>
        </DarkModeProvider>
        </FlagsProvider>
    </>
  );
};

const mapDispatchToProps = dispatch => ({
  setDistanceToBottom(distance) {
    dispatch(setDistanceToBottom({ distance }));
  },
});

export default connect(null, mapDispatchToProps)(App);
